import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { FiX, FiPlus, FiTrash2 } from 'react-icons/fi';

const BLANK_VALUE = '__blank__';

const EditCategoryModal = ({ mode = 'edit', initialCategory, prefillCategoryInput, prefillSubcategoryInput, onSuccess, onCancel }) => {
    const isCreateMode = mode === 'create';

    const [allCategories, setAllCategories] = useState([]);
    const [loadingAll, setLoadingAll] = useState(true);
    const [selectedId, setSelectedId] = useState(
        isCreateMode ? BLANK_VALUE : (initialCategory?._id || '')
    );
    const prefillTrimmed = typeof prefillSubcategoryInput === 'string' && prefillSubcategoryInput.trim();
    const prefillCategoryTrimmed = typeof prefillCategoryInput === 'string' && prefillCategoryInput.trim();
    const [categoryNameInput, setCategoryNameInput] = useState(
        initialCategory?.category || prefillCategoryTrimmed || ''
    );
    const [subcategoryList, setSubcategoryList] = useState(() => {
        const base = Array.isArray(initialCategory?.subcategory) ? [...initialCategory.subcategory] : [];
        if (prefillTrimmed && !base.includes(prefillTrimmed)) return [...base, prefillTrimmed];
        return base;
    });
    const [newSubcategoryInput, setNewSubcategoryInput] = useState('');
    const [removeConfirm, setRemoveConfirm] = useState(null); // { subcatName, categoryName }
    const [submitLoading, setSubmitLoading] = useState(false);
    const [error, setError] = useState('');

    // Title and submit: "Create" when nothing selected; "Edit" when a category is selected
    const nothingSelected = !selectedId || selectedId === BLANK_VALUE;

    // Fetch all categories for dropdown (no stats, high limit)
    useEffect(() => {
        const fetchAll = async () => {
            try {
                setLoadingAll(true);
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/foproducts/categories?page=1&limit=500`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );
                if (response.data.success && Array.isArray(response.data.data)) {
                    let list = response.data.data;
                    if (initialCategory && !list.some((c) => c._id === initialCategory._id)) {
                        list = [{ ...initialCategory }, ...list];
                    }
                    setAllCategories(list);
                    if (isCreateMode) {
                        setSelectedId(BLANK_VALUE);
                        setCategoryNameInput(prefillCategoryTrimmed || '');
                        setSubcategoryList(prefillTrimmed ? [prefillTrimmed] : []);
                    } else if (initialCategory && list.length > 0) {
                        const first = list.find((c) => c._id === initialCategory._id) || list[0];
                        setSelectedId(first._id);
                        setCategoryNameInput(first.category || '');
                        const base = Array.isArray(first.subcategory) ? [...first.subcategory] : [];
                        setSubcategoryList(prefillTrimmed && !base.includes(prefillTrimmed) ? [...base, prefillTrimmed] : base);
                    } else if (!initialCategory && list.length > 0) {
                        setSelectedId('');
                    } else if (!selectedId && list.length > 0 && selectedId !== BLANK_VALUE) {
                        const first = list[0];
                        setSelectedId(first._id);
                        setCategoryNameInput(first.category || '');
                        setSubcategoryList(Array.isArray(first.subcategory) ? [...first.subcategory] : []);
                    }
                }
            } catch (err) {
                setError(err.response?.data?.error || err.message);
            } finally {
                setLoadingAll(false);
            }
        };
        fetchAll();
    }, [mode]);

    // When dropdown (selectedId) changes, load that category's data (or clear when blank in create mode)
    useEffect(() => {
        if (!selectedId || selectedId === BLANK_VALUE || allCategories.length === 0) {
            if (isCreateMode && (selectedId === BLANK_VALUE || !selectedId)) {
                setCategoryNameInput(prefillCategoryTrimmed || '');
                setSubcategoryList(prefillTrimmed ? [prefillTrimmed] : []);
            }
            return;
        }
        const cat = allCategories.find((c) => c._id === selectedId);
        if (cat) {
            setCategoryNameInput(cat.category || '');
            const base = Array.isArray(cat.subcategory) ? [...cat.subcategory] : [];
            setSubcategoryList(prefillTrimmed && !base.includes(prefillTrimmed) ? [...base, prefillTrimmed] : base);
        }
    }, [selectedId, allCategories, isCreateMode, prefillTrimmed, prefillCategoryTrimmed]);

    const handleDropdownChange = (e) => {
        const id = e.target.value;
        setSelectedId(id);
    };

    const handleAddSubcategory = () => {
        const trimmed = newSubcategoryInput.trim();
        if (!trimmed) return;
        if (subcategoryList.includes(trimmed)) {
            setNewSubcategoryInput('');
            return;
        }
        setSubcategoryList([...subcategoryList, trimmed]);
        setNewSubcategoryInput('');
    };

    const handleKeyDownSubcategory = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddSubcategory();
        }
    };

    const handleRemoveSubcategoryClick = (subcatName) => {
        setRemoveConfirm({
            subcatName,
            categoryName: categoryNameInput
        });
    };

    const confirmRemoveSubcategory = () => {
        if (!removeConfirm) return;
        setSubcategoryList((prev) => prev.filter((s) => s !== removeConfirm.subcatName));
        setRemoveConfirm(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        // When nothing selected, treat as create: require category name
        if (nothingSelected) {
            if (!categoryNameInput || !categoryNameInput.trim()) {
                setError('Category name is required when creating a new category.');
                return;
            }
        } else {
            if (!selectedId || selectedId === BLANK_VALUE) {
                setError('Please select a category.');
                return;
            }
        }
        setSubmitLoading(true);
        try {
            const token = localStorage.getItem('token');
            const payload = {
                category: categoryNameInput.trim(),
                subcategory: subcategoryList.filter((s) => s && String(s).trim())
            };
            if (nothingSelected) {
                const response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/api/foproducts/categories`,
                    payload,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );
                if (typeof prefillSubcategoryInput === 'string' && response.data?.data?._id) {
                    const subVal = prefillSubcategoryInput.trim() || newSubcategoryInput.trim();
                    onSuccess(response.data.data._id, subVal, categoryNameInput.trim(), payload.subcategory);
                } else {
                    onSuccess();
                }
            } else {
                await axios.put(
                    `${process.env.REACT_APP_API_URL}/api/foproducts/categories/${selectedId}`,
                    payload,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );
                if (typeof prefillSubcategoryInput === 'string') {
                    const subVal = prefillSubcategoryInput.trim() || newSubcategoryInput.trim();
                    onSuccess(selectedId, subVal, categoryNameInput.trim(), subcategoryList);
                } else {
                    onSuccess();
                }
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl my-8">
                <div className="flex justify-between items-center border-b pb-4 mb-4">
                    <h2 className="text-xl font-bold flex items-center gap-2 flex-wrap">
                        {nothingSelected ? 'Create Category' : 'Edit Category'} -
                        <select
                            value={selectedId || ''}
                            onChange={handleDropdownChange}
                            className="px-3 py-1.5 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-lg font-semibold text-blue-600"
                            disabled={loadingAll}
                        >
                            {isCreateMode && (
                                <option value={BLANK_VALUE}>
                                    (New category)
                                </option>
                            )}
                            {!isCreateMode && !initialCategory && (
                                <option value="">
                                    Select category...
                                </option>
                            )}
                            {allCategories.map((c) => (
                                <option key={c._id} value={c._id}>
                                    {c.category || '(Unnamed)'}
                                </option>
                            ))}
                        </select>
                    </h2>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="text-gray-500 hover:text-gray-700 p-1"
                        aria-label="Close"
                    >
                        <FiX className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
                            {error}
                        </div>
                    )}

                    <div className="space-y-4">
                        <div className="flex items-center gap-2 flex-wrap">
                            <label className="text-sm font-medium text-gray-700 shrink-0">
                                Category {nothingSelected && '*'}:
                            </label>
                            <input
                                type="text"
                                required={nothingSelected}
                                value={categoryNameInput}
                                onChange={(e) => setCategoryNameInput(e.target.value)}
                                className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                placeholder="e.g., accessories, bag, watches"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Subcategories (Optional)
                            </label>
                            <div className="flex gap-2 mb-2">
                                <input
                                    type="text"
                                    value={newSubcategoryInput}
                                    onChange={(e) => setNewSubcategoryInput(e.target.value)}
                                    onKeyDown={handleKeyDownSubcategory}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="e.g., belt, cap — press Enter or + to add"
                                />
                                <button
                                    type="button"
                                    onClick={handleAddSubcategory}
                                    className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center shrink-0"
                                    title="Add subcategory"
                                >
                                    <FiPlus className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {subcategoryList.map((subcat) => (
                                    <span
                                        key={subcat}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 rounded-md text-sm"
                                    >
                                        {subcat}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveSubcategoryClick(subcat)}
                                            className="text-red-600 hover:text-red-800 p-0.5 rounded"
                                            title={`Remove ${subcat}`}
                                        >
                                            <FiTrash2 className="w-4 h-4" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-6">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitLoading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                            {submitLoading
                                ? (nothingSelected ? 'Creating...' : 'Updating...')
                                : (nothingSelected ? 'Create' : 'Update')}
                        </button>
                    </div>
                </form>
            </div>

            {/* Confirm remove subcategory */}
            {removeConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-[60]">
                    <div className="bg-white rounded-lg p-6 max-w-md shadow-xl mx-4">
                        <p className="text-gray-700 mb-4">
                            Remove <strong>{removeConfirm.subcatName}</strong> from{' '}
                            <strong>{removeConfirm.categoryName}</strong>?
                        </p>
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setRemoveConfirm(null)}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmRemoveSubcategory}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

EditCategoryModal.propTypes = {
    mode: PropTypes.oneOf(['create', 'edit']),
    initialCategory: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        category: PropTypes.string,
        subcategory: PropTypes.arrayOf(PropTypes.string)
    }),
    prefillCategoryInput: PropTypes.string,
    prefillSubcategoryInput: PropTypes.string,
    onSuccess: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired
};

export default EditCategoryModal;
